import React from 'react';
import { ListItemText } from '@material-ui/core';
import { DocsSearchResult } from '../search/useDocsSearch';
import {
  DropdownPaper,
  DropdownList,
  DropdownListItem,
  ResultTitle,
  ResultContext,
  TabChip,
  HighlightSpan,
  EmptyMessage,
} from './SearchDropdown.styles';

const TAB_LABELS: Record<string, string> = {
  home: 'Home',
  'getting-started': 'Getting Started',
  'landing-page-guide': 'Landing Page Guide',
  'service-management-api': 'Service management API',
  help: 'Help',
  'release-notes': 'Release Notes',
};

const CONTEXT_CHARS = 80;

function getTabLabel(location: string): string {
  const tabId = location.replace(/^\/docs-v2\//, '').split('/')[0] || 'home';
  return TAB_LABELS[tabId] || tabId;
}

function extractContext(
  text: string,
  term: string,
): string | undefined {
  if (!term || term.length < 2) return undefined;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return undefined;

  const start = Math.max(0, idx - CONTEXT_CHARS);
  const end = Math.min(text.length, idx + term.length + CONTEXT_CHARS);
  const prefix = start > 0 ? '...' : '';
  const suffix = end < text.length ? '...' : '';
  return `${prefix}${text.slice(start, end)}${suffix}`;
}

interface SearchDropdownProps {
  results: DocsSearchResult[];
  term: string;
  isSearching: boolean;
  onSelect: (result: DocsSearchResult) => void;
}

function highlightTerm(text: string, term: string) {
  if (!term || term.length < 2) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const splitRegex = new RegExp(`(${escaped})`, 'gi');
  const testRegex = new RegExp(`^${escaped}$`, 'i');
  const parts = text.split(splitRegex);
  return parts.map((part, i) =>
    testRegex.test(part) ? (
      <HighlightSpan key={i}>{part}</HighlightSpan>
    ) : (
      part
    ),
  );
}

function groupByTab(
  results: DocsSearchResult[],
): Record<string, DocsSearchResult[]> {
  const groups: Record<string, DocsSearchResult[]> = {};
  for (const r of results) {
    const tab = getTabLabel(r.location);
    if (!groups[tab]) groups[tab] = [];
    groups[tab].push(r);
  }
  return groups;
}

export function SearchDropdown({
  results,
  term,
  isSearching,
  onSelect,
}: SearchDropdownProps) {
  if (!term || term.length < 2) return null;
  if (isSearching) return null;

  if (results.length === 0) {
    return (
      <DropdownPaper elevation={4}>
        <EmptyMessage>
          No results found for &quot;{term}&quot;
        </EmptyMessage>
      </DropdownPaper>
    );
  }

  const grouped = groupByTab(results);

  return (
    <DropdownPaper elevation={4}>
      <DropdownList>
        {Object.entries(grouped).map(([tab, items]) => (
          <React.Fragment key={tab}>
            {items.map((item, idx) => {
              const context = extractContext(item.text, term);
              return (
                <DropdownListItem
                  key={`${item.location}-${idx}`}
                  button
                  onClick={() => onSelect(item)}
                  divider
                >
                  <ListItemText
                    disableTypography
                    primary={
                      <div>
                        <TabChip
                          label={tab.toLowerCase()}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <ResultTitle>
                          {highlightTerm(item.title, term)}
                        </ResultTitle>
                      </div>
                    }
                    secondary={
                      context ? (
                        <ResultContext>
                          {highlightTerm(context, term)}
                        </ResultContext>
                      ) : null
                    }
                  />
                </DropdownListItem>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownList>
    </DropdownPaper>
  );
}
